import { useState } from 'react';

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const msg = (form.querySelector('[name="message"]') as HTMLTextAreaElement)?.value || '';
    if (msg.length > 500) return;
    setSubmitting(true);
    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((v, k) => params.append(k, v.toString()));
    try {
      await fetch('https://readdy.ai/api/form/d71nh8ohb0rqicn68kq0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <span className="text-red-500 text-sm font-bold tracking-widest uppercase">Contact</span>
          <h2 className="text-4xl font-black text-gray-900 mt-3 mb-4">お問い合わせ</h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto leading-relaxed">
            導入のご相談・資料請求・デモのご依頼など、お気軽にお問い合わせください。<br />
            担当者より1営業日以内にご連絡いたします。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-10 items-start">
          {/* Left info */}
          <div className="md:col-span-2 flex flex-col gap-7">
            <div className="bg-white rounded-xl p-7">
              <h3 className="text-gray-800 font-bold text-base mb-5">お問い合わせ方法</h3>
              <div className="flex flex-col gap-5">
                {[
                  { icon: 'ri-mail-line', label: 'メール', val: 'info@storad.jp', color: 'bg-red-100 text-red-500' },
                  { icon: 'ri-phone-line', label: '電話', val: '0120-XXX-XXX', color: 'bg-orange-100 text-orange-500' },
                  { icon: 'ri-time-line', label: '対応時間', val: '平日 10:00〜18:00', color: 'bg-green-100 text-green-600' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${item.color}`}>
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    <div>
                      <div className="text-gray-400 text-xs">{item.label}</div>
                      <div className="text-gray-800 font-semibold text-sm">{item.val}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-7 text-white">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg mb-4">
                <i className="ri-customer-service-2-line text-xl"></i>
              </div>
              <h4 className="font-bold text-base mb-2">無料相談受付中</h4>
              <p className="text-red-100 text-sm leading-relaxed">
                「どのプランが合うか分からない」「まず話を聞いてみたい」という方もお気軽にどうぞ。
              </p>
            </div>
          </div>

          {/* Right form */}
          <div className="md:col-span-3 bg-white rounded-xl p-8">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full">
                  <i className="ri-check-line text-green-500 text-3xl"></i>
                </div>
                <h3 className="text-gray-800 font-bold text-lg">送信完了しました</h3>
                <p className="text-gray-500 text-sm">1営業日以内にご連絡いたします。</p>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="text-gray-700 text-sm font-semibold mb-1.5 block">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="name"
                      required
                      type="text"
                      placeholder="山田 太郎"
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-gray-700 text-sm font-semibold mb-1.5 block">
                      会社・店舗名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="company"
                      required
                      type="text"
                      placeholder="株式会社〇〇"
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-gray-700 text-sm font-semibold mb-1.5 block">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="email"
                    required
                    type="email"
                    placeholder="example@company.co.jp"
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-gray-700 text-sm font-semibold mb-1.5 block">電話番号</label>
                  <input
                    name="phone"
                    type="tel"
                    placeholder="03-XXXX-XXXX"
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-gray-700 text-sm font-semibold mb-1.5 block">
                    お問い合わせ種別 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="inquiry_type"
                    required
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors bg-white cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="資料請求">資料請求</option>
                    <option value="デモ依頼">デモ依頼</option>
                    <option value="料金相談">料金相談</option>
                    <option value="導入相談">導入相談</option>
                    <option value="その他">その他</option>
                  </select>
                </div>
                <div>
                  <label className="text-gray-700 text-sm font-semibold mb-1.5 flex justify-between">
                    <span>お問い合わせ内容</span>
                    <span className={`text-xs ${charCount > 500 ? 'text-red-500' : 'text-gray-400'}`}>{charCount}/500</span>
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    placeholder="ご質問・ご要望などをご記入ください（500文字以内）"
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting || charCount > 500}
                  className="cursor-pointer whitespace-nowrap w-full bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-bold py-3.5 rounded-md text-sm transition-colors"
                >
                  {submitting ? '送信中...' : '送信する'}
                </button>
                <p className="text-gray-400 text-xs text-center">
                  送信いただいた個人情報は、お問い合わせへの回答にのみ使用します。
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
